<template>
  <div
    v-if="open"
    class="member-popup"
    :class="{ 'member-popup--half': halfScreen }"
    role="dialog"
    aria-modal="true"
    aria-labelledby="member-popup-title"
    @click.self="close"
  >
    <div class="member-popup__card">
      <button
        type="button"
        class="member-popup__close"
        aria-label="Close popup"
        @click="close"
      >
        <i class="icon icon-cross" />
      </button>

      <picture class="member-popup__media">
        <source media="(min-width: 992px)" :srcset="desktopImage" />
        <img class="member-popup__image" :src="mobileImage" alt="" />
      </picture>

      <div class="member-popup__body">
        <div class="member-popup__content">
          <h2 id="member-popup-title" class="member-popup__title">
            Exclusive member discounts
          </h2>
          <p class="member-popup__copy">
            Earn points with every purchase to redeem against future orders,
            plus unlock benefits including early sale access, free shipping and
            exclusive concierge services.
          </p>
          <p class="member-popup__copy">
            Join now for £20 off your first purchase.
          </p>
          <a class="member-popup__cta" href="/account/register" @click="close">
            Get started
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFeatureToggles } from "@/helpers/featureToggles.js";

const DISMISS_KEY = "memberDiscountPopupDismissed";

export default {
  name: "MemberDiscountPopup",
  props: {
    open: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close"],
  data() {
    return {
      mobileImage:
        "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_1_1_2482ee1a-1e2b-4569-bd48-09b279fb726b.jpg?v=1790942312",
      desktopImage:
        "https://cdn.shopify.com/s/files/1/0942/9062/files/GREEN_SEQUIN_DRESS_website_portrait_01_1_2_a33724ea-e3e2-48dc-8dbf-afb3b0390861.jpg?v=1790942485",
    };
  },
  computed: {
    halfScreen() {
      const { memberDiscountMobile = "full" } = getFeatureToggles();
      return memberDiscountMobile === "half";
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        localStorage.setItem(DISMISS_KEY, "true");
      }
    },
  },
  mounted() {
    window.addEventListener("keydown", this.onKeydown);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown);
  },
  methods: {
    close() {
      localStorage.setItem(DISMISS_KEY, "true");
      this.$emit("close");
    },
    onKeydown(event) {
      if (this.open && event.key === "Escape") {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.member-popup {
  position: fixed;
  inset: 0;
  z-index: 1002;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(10px);
}

.member-popup__card {
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(390px, 100%);
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
}

.member-popup__close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  background: none;
  color: #fff;
  cursor: pointer;

  .icon {
    font-size: 12px;
  }
}

.member-popup__media {
  display: block;
  flex-shrink: 0;
}

.member-popup__image {
  display: block;
  width: 100%;
  height: 280px;
  object-fit: cover;
  object-position: center 20%;
}

.member-popup__body {
  padding: 28px 24px 32px;
  text-align: center;
}

.member-popup__title {
  margin: 0 0 16px;
  @include editorial-headline(24px);
  line-height: 1;
  text-transform: uppercase;
  color: #000;
}

.member-popup__copy {
  margin: 0 0 16px;
  @include editorial-body(14px);
  line-height: 1.5;
  color: #000;

  &:last-of-type {
    margin-bottom: 24px;
  }
}

.member-popup__cta {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 48px;
  padding: 15px 28px;
  border-radius: 4px;
  background: #000;
  color: #fff;
  text-decoration: none;
  @include utility-body(13px);
  line-height: 1.35;
  text-transform: uppercase;
}

@include respond-above(lg) {
  .member-popup {
    padding: 24px;
  }

  .member-popup__card {
    flex-direction: row;
    width: min(1014px, calc(100vw - 48px));
    height: min(680px, calc(100vh - 48px));
    overflow: hidden;
  }

  .member-popup__close {
    top: 20px;
    right: 20px;
    color: #000;
  }

  .member-popup__media {
    width: 50%;
    height: 100%;
  }

  .member-popup__image {
    height: 100%;
  }

  .member-popup__body {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 50%;
    padding: 48px 55px;
  }

  .member-popup__content {
    width: 100%;
    max-width: 392px;
  }

  .member-popup__title {
    @include editorial-headline(32px);
  }
}

@include respond-below(lg) {
  .member-popup:not(.member-popup--half) {
    padding: 0;

    .member-popup__card {
      width: 100%;
      height: 100%;
      max-height: none;
      overflow: hidden;
    }

    .member-popup__media {
      flex: 1;
      min-height: 180px;
    }

    .member-popup__image {
      height: 100%;
    }

    .member-popup__body {
      flex-shrink: 0;
    }
  }

  .member-popup--half {
    align-items: flex-end;
    padding: 0;

    .member-popup__card {
      width: 100%;
      max-height: none;
    }

    .member-popup__media {
      display: none;
    }

    .member-popup__close {
      top: 16px;
      right: 16px;
      color: #000;
    }

    .member-popup__body {
      padding: 48px 20px 24px;
    }
  }
}
</style>
